
import React, { useState, useEffect, startTransition } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play, Settings, Clock, Zap, RefreshCw, Users, Shield } from 'lucide-react';

export function HeroSection() {
  const headlines = [
    "Built-In AI Agents. Built to Sell.",
    "AI Agents, Native Calling, and Zero Guesswork.",
    "Close Deals Without Lifting a Finger."
  ];

  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  useEffect(() => {
    const currentHeadline = headlines[currentHeadlineIndex];
    
    if (isTyping) {
      if (displayedText.length < currentHeadline.length) {
        const timeout = setTimeout(() => {
          startTransition(() => {
            setDisplayedText(currentHeadline.slice(0, displayedText.length + 1));
          });
        }, 100); // Slower typing speed
        return () => clearTimeout(timeout);
      } else {
        // Finished typing, wait before starting to delete
        const timeout = setTimeout(() => {
          startTransition(() => {
            setIsTyping(false);
          });
        }, 3000); // Longer display time
        return () => clearTimeout(timeout);
      }
    } else {
      if (displayedText.length > 0) {
        const timeout = setTimeout(() => {
          startTransition(() => {
            setDisplayedText(displayedText.slice(0, -1));
          });
        }, 50); // Deleting speed
        return () => clearTimeout(timeout);
      } else {
        // Finished deleting, move to next headline
        startTransition(() => {
          setCurrentHeadlineIndex((prev) => (prev + 1) % headlines.length);
          setIsTyping(true);
        });
      }
    }
  }, [displayedText, isTyping, currentHeadlineIndex, headlines]);

  // Function to render text with colored punctuation
  const renderTextWithColoredPunctuation = (text: string) => {
    return text.split('').map((char, index) => {
      if (char === ',' || char === '.') {
        return (
          <span key={index} className="text-blue-400">
            {char}
          </span>
        );
      }
      return <span key={index}>{char}</span>;
    });
  };

  const features = [
    { text: "Custom Solutions", icon: Settings },
    { text: "24/7 Support", icon: Clock },
    { text: "Fast Development", icon: Zap },
    { text: "Regular Updates", icon: RefreshCw },
    { text: "Project Management", icon: Users },
    { text: "Security First", icon: Shield }
  ];

  return (
    <div className="relative pt-32 pb-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight min-h-[200px] md:min-h-[280px] flex items-center justify-center">
            <span className="text-yellow-400 relative">
              {renderTextWithColoredPunctuation(displayedText)}
              <span className="animate-pulse">|</span>
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed">
            From lead gen to outreach to deal close—your AI-powered sales engine runs on autopilot.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <Button asChild size="lg" className="bg-yellow-400 text-neutral-900 hover:bg-yellow-300 text-lg px-8 py-4 rounded-full font-semibold">
              <Link to="/register" className="inline-flex items-center gap-x-3">
                Get started free
                <ArrowRight className="w-5 h-5" />
              </Link>
            </Button>
            <Button variant="outline" size="lg" className="border-border text-foreground hover:bg-accent hover:text-accent-foreground text-lg px-8 py-4 rounded-full" asChild>
              <Link to="/contact" className="inline-flex items-center gap-x-3">
                <Play className="w-5 h-5" />
                Watch demo
              </Link>
            </Button>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="text-center">
                <div className="flex justify-center items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400/20 to-yellow-400/10 border border-yellow-400/20 mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-yellow-400" />
                </div>
                <div className="mt-3">
                  <h3 className="text-sm font-semibold text-muted-foreground">{feature.text}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
